import { useRef, useState } from "react";
import { Upload, Loader2, Images, Check } from "lucide-react";
import { toast } from "sonner";
import { api, photoUrl } from "@/lib/api";

function Slot({ label, photoId, onUpload, uploading, testid, choices = [], onPick }) {
  const ref = useRef(null);
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>
      <button
        type="button"
        onClick={() => ref.current?.click()}
        data-testid={testid}
        className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-white/15 hover:border-[#ff6b00]/60 overflow-hidden flex items-center justify-center bg-[#0f1522]"
      >
        {photoId ? (
          <img src={photoUrl(photoId)} alt={label} className="w-full h-full object-cover" />
        ) : uploading ? (
          <Loader2 className="h-6 w-6 text-[#ff6b00] animate-spin" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-slate-400 text-xs">
            <Upload className="h-5 w-5 text-[#ff6b00]" /> Upload
          </div>
        )}
      </button>
      <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} data-testid={`${testid}-input`} />
      {choices.length > 0 && (
        <div className="mt-2 flex gap-1.5 flex-wrap">
          {choices.map((p) => (
            <button key={p.id} type="button" onClick={() => onPick(p.id)} data-testid={`${testid}-pick-${p.id}`} className={`h-10 w-10 rounded-md overflow-hidden border ${photoId === p.id ? "border-[#ff6b00]" : "border-white/10"}`}>
              <img src={photoUrl(p.id)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PublishShowcase({ booking, onPublished }) {
  const [before, setBefore] = useState(booking?.photos?.[0]?.id || "");
  const [after, setAfter] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState(false);

  const upload = async (file, which) => {
    setBusy(which);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", which);
      const { data } = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      (which === "before" ? setBefore : setAfter)(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      await api.post("/gallery", {
        booking_id: booking?.id || null,
        before_photo_id: before,
        after_photo_id: after,
        caption,
        service_name: booking?.service_name || "",
        vehicle_label: booking ? `${booking.vehicle.year} ${booking.vehicle.make} ${booking.vehicle.model}` : "",
        published: true,
      });
      setDone(true);
      toast.success("Published to the showcase");
      onPublished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="cj-card p-4" data-testid="publish-showcase">
      <div className="flex items-center gap-2 mb-3">
        <Images className="h-4 w-4 text-[#ff6b00]" />
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Before / After showcase</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Slot label="Before" photoId={before} uploading={busy === "before"} onUpload={(f) => upload(f, "before")} testid="showcase-before" choices={booking?.photos || []} onPick={setBefore} />
        <Slot label="After" photoId={after} uploading={busy === "after"} onUpload={(f) => upload(f, "after")} testid="showcase-after" />
      </div>
      <input
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        placeholder="Caption — e.g. 3 years of dog hair, gone."
        data-testid="showcase-caption-input"
        className="mt-3 w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-[#ff6b00]"
      />
      <button
        onClick={publish}
        disabled={!before || !after || !!busy || done}
        data-testid="showcase-publish-button"
        className="cj-btn-primary mt-3 w-full px-4 py-2.5 rounded-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {done ? <><Check className="h-4 w-4" /> Published</> : busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish to gallery"}
      </button>
    </div>
  );
}
